import type { JSX } from 'react';
import React from 'react';

import {
  Box,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  SvgIcon
} from '@mui/material';
import type { AnyAction, Dispatch } from 'redux';

import { updateField } from '../../store/customer';

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface FormikLike {
  field: {
    value: unknown;
  };
  form: {
    setFieldValue: (name: string, value: unknown) => void;
  };
}

interface ApiService {
  updateCustomer: (customer: Record<string, unknown>, dispatch: Dispatch<AnyAction>) => void;
}

interface FieldRadioProps {
  formik: FormikLike;
  options?: Option[];
  name: string;
  required?: boolean;
  label?: string;
  dispatch: Dispatch<AnyAction>;
  store: { customer: Record<string, unknown> };
  apiService: ApiService;
  disableAutoSave?: boolean;
}

function SproutRadioIcon({
  checked
}: {
  checked: boolean;
}): JSX.Element {
  return (
    <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 24 }}>
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke={checked ? 'var(--brand-leaf-green)' : 'var(--neutral-400)'}
        strokeWidth="2"
      />
      {checked && (
        <circle
          cx="12"
          cy="12"
          r="5"
          fill="var(--brand-leaf-green)"
        />
      )}
    </SvgIcon>
  );
}

function FieldRadio({
  formik,
  options = [],
  name,
  required,
  label,
  dispatch,
  store,
  apiService,
  disableAutoSave
}: FieldRadioProps): JSX.Element {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    dispatch(updateField({ name, value }));
    formik.form.setFieldValue(name, value);

    // Trigger update request to the API
    if (!disableAutoSave) {
      apiService.updateCustomer(store.customer, dispatch);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%'
      }}
    >
      {label && (
        <FormLabel
          required={required}
          sx={{
            display: 'block',
            font: 'var(--sprout-font-body1)',
            color: 'var(--Text-text-primary, #101C16)',
            margin: '0 0 8px',
            '& .MuiFormLabel-asterisk': {
              color: 'var(--Text-text-danger, #CD0C14)'
            }
          }}
        >
          {label}
        </FormLabel>
      )}

      <RadioGroup
        onChange={handleChange}
        value={formik.field.value as string}
        row={false}
      >
        {options.map(option => (
          <FormControlLabel
            key={`${name}-${option.label}`}
            value={option.value}
            disabled={option.disabled}
            control={
              <Radio
                icon={<SproutRadioIcon checked={false} />}
                checkedIcon={<SproutRadioIcon checked={true} />}
                disableRipple
                sx={{
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 125, 52, 0.08)'
                  },
                  '&.Mui-disabled': {
                    color: 'var(--neutral-300)'
                  }
                }}
              />
            }
            label={option.label}
            sx={{
              '.MuiFormControlLabel-label': {
                color: 'var(--Text-text-primary, #101C16)',
                font: 'var(--sprout-font-body1)',
                ml: '8px'
              },
              mb: '16px'
            }}
          />
        ))}
      </RadioGroup>
    </Box>
  );
}

export default FieldRadio;